namespace Solovis.Reports.Formatter {
  function getContextInformation(valueContext) {
    const valueConfig = valueContext && valueContext.group ? valueContext.group : valueContext && valueContext.col ? valueContext.col : {};
    const dataType = valueConfig.dataType ? valueConfig.dataType : (valueContext && valueContext.dataType ? valueContext.dataType : null);
    const formatHint = valueConfig.formatHint ? valueConfig.formatHint : valueContext && valueContext.formatHint ? valueContext.formatHint : {};
    return { dataType, formatHint, valueConfig };
  }

  function addSeperatorInPrintMode(arrayPosition: number, printMode: boolean, values: any[]) {
    try {
      if (values && values.length > 0) {
        if (printMode === true && arrayPosition < values.length - 1) {
          return ';';
        }
      }
    }
    catch (e) {
      console.error(e);
    }
    return "";
  }

  export function formatDayIdValueAsHtml(value, valueContext) {
    let dateString = "";
    if (typeof value === "number") {
      if (value < 0) {
        // We don't currently support converting to dates less than 1900 on JS side.
        return "<span>&lt; 1900 (" + value + ")</span>";
      }

      const date = InvData.Util.DateIndexHelper.getDateFromDateIndex(value);

      const { formatHint } = getContextInformation(valueContext);

      if (formatHint.dateFormat) {
        if (formatHint.dateFormat == "yyyy") {
          // year
          dateString = date.getFullYear().toString();
        }
        else if (formatHint.dateFormat == "q yyyy") {
          // quarter year
          const quarter = (1 + (Math.floor(date.getMonth() / 3))).toString();
          dateString = quarter + "Q " + date.getFullYear().toString();
        }
        else {
          dateString = kendo.toString(date, formatHint.dateFormat);
        }
      }

      dateString = kendo.toString(date, "MMM d, yyyy");
    }

    return htmlEncode(dateString);
  }

  export function formatBoolValueAsHtml(value, valueContext) {
    if (value === undefined || value === null) {
      return "";
    }

    const { formatHint } = getContextInformation(valueContext);

    return value
      ? formatHint && formatHint.yesHtml ? formatHint.yesHtml : "Yes"
      : formatHint && formatHint.noHtml ? formatHint.noHtml : "No";
  }

  export function formatCurrencyIdValueAsHtml(value, valueContext) {
    if (!value) return null;
    return valueContext.modelEnvironment ? valueContext.modelEnvironment.labelHelper.getCurrencyCodeLabel(value) : htmlEncode(value);
  }

  export function formatResourceIdValueAsHtml(value, valueContext) {
    if (!value) return null;
    return valueContext.modelEnvironment ? valueContext.modelEnvironment.labelHelper.getResourceLabel(value) : htmlEncode(toString());
  }

  function formatNumberAsHtml(value, valueContext): string | JQuery | null {
    const { valueConfig, dataType } = getContextInformation(valueContext);

    const defaultFormatHint = {
      scale: dataType === "number" ? null : 2,
      absoluteZeroEpsilon: 0,
      colorCodeNegativeNumber: dataType === "number" ? false : true,
      noValueHtml: "<span class='null-number-value' title='No Value'>NV</span>",
    }
    const formatHint = {
      ...defaultFormatHint,
      ...(valueConfig.formatHint ? valueConfig.formatHint : valueContext && valueContext.formatHint ? valueContext.formatHint : {}),
    }

    if (typeof value != "number") {
      return formatHint.noValueHtml;
    }
    if (isNaN(value)) {
      return null;
    }

    if (Math.abs(value) <= formatHint.absoluteZeroEpsilon) {
      // We treat anything less than this epsilon as absolute zero (which is displayed as double dashes).
      return "<span class='zero-number-value' title='Zero'>--</span>";
    }

    if (Solovis.isNull(formatHint.colorCodeNegatives)) {
      formatHint.colorCodeNegatives = defaultFormatHint.colorCodeNegativeNumber;
    }

    let decimalPlaces = (formatHint.scale >= 0 && formatHint.scale <= 100) ? formatHint.scale : null;

    let formattedNumber = null;

    if (formatHint.numeraljsFormat) {
      formattedNumber = numeral(value).format(formatHint.numeraljsFormat);
    } else {
      if (dataType === "number" && decimalPlaces === null) {
        formattedNumber = InvData.FormatterHelper.getBasicNumberFormatter().format(value);
      } else if (dataType === "percentage" || dataType === "hybridReturn") {
        formattedNumber = InvData.FormatterHelper.getPercentageFormatter({ decimalPlaces: decimalPlaces }).format(value);
      } else {
        formattedNumber = InvData.FormatterHelper.getNumberFormatter({ decimalPlaces: decimalPlaces }).format(value);
      }
      formattedNumber = htmlEncode(formattedNumber);
    }

    return (formatHint.colorCodeNegativeNumber && value < 0) ? $("<span class='negative-number-value'>").html(formattedNumber) : formattedNumber;
  }

  function formatEntityIdAsHtml(value, valueContext) {
    if (!value)
      return null;

    const { formatHint } = getContextInformation(valueContext);

    var entityLabel = value;

    if (valueContext.modelEnvironment) {
      entityLabel = valueContext.modelEnvironment.labelHelper.getEntityLabel(value);
    }

    if (formatHint && formatHint.standardLinks) {
      const entityLink = $("<a>").text(entityLabel);

      Solovis.UI.PopupLinkPanel.onElementClick(entityLink, function (e) {
        const links = Solovis.Reports.ReportLinker.getEntityLinks({ entityId: value });

        Solovis.UI.PopupLinkPanel.showLinks({
          links: links,
          autoClickSingleItem: e.type == "click",
          offset: { left: e.pageX, top: e.pageY }
        });
      });

      Solovis.Site.DragDropManager.setupSolovisRefDrag(entityLink, { type: "entity", value: { entityId: value } });

      return entityLink;
    }
    else if (formatHint && formatHint.customLinks) {
      var entityLink = $("<a>").text(entityLabel);

      Solovis.UI.PopupLinkPanel.onElementClick(entityLink, function (e) {
        const links = formatHint.customLinks(value, valueContext);

        Solovis.UI.PopupLinkPanel.showLinks({
          links: links,
          autoClickSingleItem: e.type == "click",
          offset: { left: e.pageX, top: e.pageY }
        });
      });

      Solovis.Site.DragDropManager.setupSolovisRefDrag(entityLink, { type: "entity", value: { entityId: value } });

      return entityLink;
    }
    else {
      return htmlEncode(entityLabel);
    }
  }

  function formatInvestmentIdAsHtml(value, valueContext) {
    if (!value)
      return null;

    const { formatHint } = getContextInformation(valueContext);

    let investmentLabel = value;

    if (valueContext.modelEnvironment) {
      investmentLabel = valueContext.modelEnvironment.labelHelper.getInvestmentLabel(value);
    }

    if (formatHint && formatHint.standardLinks) {
      const investmentLink = document.createElement("a");
      investmentLink.textContent = investmentLabel;

      Solovis.UI.PopupLinkPanel.onElementClick(investmentLink, function (e) {
        const links = Solovis.Reports.ReportLinker.getInvestmentLinks({ investmentId: value });
        Solovis.UI.PopupLinkPanel.showLinks({
          links: links,
          autoClickSingleItem: e.type == "click",
          offset: { left: e.pageX, top: e.pageY }
        });
      });

      Solovis.Site.DragDropManager.setupSolovisRefDrag(investmentLink, { type: "investment", value: { investmentId: value } });

      return investmentLink;
    }
    else {
      return htmlEncode(investmentLabel);
    }
  }
  function formatTagIdAsHtml(value, valueContext) {
    const { valueConfig, formatHint } = getContextInformation(valueContext);
    if (!value) {
      if (formatHint && formatHint.showUnknownIfNoValue) {
        return "Unknown";
      }

      return null;
    }

    if (valueContext.modelEnvironment) {
      if (valueConfig.tagSetId) {
        const tagSet = valueContext.modelEnvironment.tagSets.tryGetById(valueConfig.tagSetId);
        if (tagSet) {
          const tag = tagSet.tryGetTagById(value);
          if (tag) {
            if (formatHint && formatHint.standardLinks) {
              const tagLink = document.createElement("a");
              tagLink.textContent = tag.label;

              Solovis.UI.PopupLinkPanel.onElementClick(tagLink, function (e) {
                const links = Solovis.Reports.ReportLinker.getTagLinks({ tagId: value, tagSetId: valueConfig.tagSetId });
                Solovis.UI.PopupLinkPanel.showLinks({
                  links: links,
                  offset: { left: e.pageX, top: e.pageY }
                });
              });

              return tagLink;
            }

            var tagLabelSpan = document.createElement("span");
            tagLabelSpan.textContent = tag.label;
            return tagLabelSpan;
          }
        }
      }
    }
    return htmlEncode(value);
  }

  function formatPropertyDefinitionIdAsHtml(value, valueContext) {
    if (!value)
      return null;

    if (valueContext.modelEnvironment) {
      var currencyCodeLabel = valueContext.modelEnvironment.labelHelper.getPropertyDefinitionLabel(value);
      return currencyCodeLabel;
    }

    return htmlEncode(value);
  }

  function formatOwnershipChainAsHtml(value, valueContext) {
    if (!value)
      return null;

    if (valueContext.modelEnvironment) {
      var chainDiv = $("<div>");
      var chainIndex = 0;

      var chain = value;

      var chainItems = [];

      while (chain) {
        chainItems.splice(0, 0, chain);

        chain = chain.nextLevel;
      }

      // Iterating over the chain items, highest level to lowest level.
      $(chainItems).each(function (chainIndex, chain: any) {
        if (chainIndex > 0) {
          chainDiv.append(document.createTextNode(", "));
        }

        var chainSegmentSpan = $("<span>").appendTo(chainDiv);
        var investmentId = chain.investmentId;

        var investmentLabel = valueContext.modelEnvironment.labelHelper.getInvestmentLabel(investmentId);
        var investmentLink = $("<a>").text(investmentLabel);

        Solovis.UI.PopupLinkPanel.onElementClick(investmentLink, function (e) {
          var links = Solovis.Reports.ReportLinker.getInvestmentLinks({ investmentId: investmentId });
          Solovis.UI.PopupLinkPanel.showLinks({
            links: links,
            offset: { left: e.pageX, top: e.pageY }
          });
        });

        chainSegmentSpan.append(investmentLink);
      });

      return chainDiv;
    }
    else {
      return "ownershipChain";
    }
  }

  function formatUtcTimestampAsHtml(value, valueContext) {
    if (typeof value == "number") {
      try {
        // NOTE: This will display utcTimestamp in local time.
        return kendo.toString(new Date(value), "M/d/yyyy h:mm:ss tt");
      }
      catch (ex) {
        return "error";
      }
    }
    else if (Solovis.isNotNull(value)) {
      return htmlEncode(value.toString());
    }
    else {
      return "";
    }
  }

  function formatValueTypeIdAsHtml(value, valueContext) {
    if (typeof value == "undefined" || value === null)
      return "";

    var valueStr = Formatter.getValueTypeString(value);

    return htmlEncode(valueStr);
  }

  function formatVisualizationAsHtml(value, valueContext) {
    return getVisualizationDivForCellValue(value);
  }

  function formatPropertyValueInfoAsHtml(value, valueContext) {
    if (value) {
      const prop = valueContext.modelEnvironment.propertyDefinitions.idToItemMap[value.propertyDefinitionId];
      return Solovis.Reports.Formatter.formatResourcePropertyValueAsHtmlRaw(
        prop.propertyTypeId,
        value.value,
        value.valueStrings,
        prop.tagRefTagSetId,
        valueContext.dataTable && valueContext.dataTable.printMode);
    }
  }

  function htmlEncode(text) {
    return $("<div>").text(text).html();
  }

  export function formatValueAsHtml(value, valueContext) {
    return getHtmlFormatter(valueContext)(value, valueContext);
  }

  export function formatHybridReturn(value, valueContext) {
    valueContext.defaultFormatter = (val, vc) => formatNumberAsHtml(val, vc);
    return formatCumulativeReturn(value, valueContext);
  }

  export function getHtmlFormatter(valueContext) {
    const { dataType } = getContextInformation(valueContext);

    const defaultFormatter = (value, valueContext) => value === undefined || value === null ? "" : htmlEncode(value.toString());
    if (!dataType) {
      return defaultFormatter;
    }

    const dataTypeFormatters = {
      "dayId": formatDayIdValueAsHtml,
      "bool": formatBoolValueAsHtml,
      "percentage": formatNumberAsHtml,
      "currency": formatNumberAsHtml,
      "number": formatNumberAsHtml,
      "currencyId": formatCurrencyIdValueAsHtml,
      "resourceId": formatResourceIdValueAsHtml,
      "entityId": formatEntityIdAsHtml,
      "investmentId": formatInvestmentIdAsHtml,
      "tagId": formatTagIdAsHtml,
      "propertyDefinitionId": formatPropertyDefinitionIdAsHtml,
      "ownershipChain": formatOwnershipChainAsHtml,
      "utcTimestamp": formatUtcTimestampAsHtml,
      "valueTypeId": formatValueTypeIdAsHtml,
      "visualization": formatVisualizationAsHtml,
      "propertyValueInfo": formatPropertyValueInfoAsHtml,
      "hybridReturn": formatHybridReturn,
    }

    return dataTypeFormatters[dataType] || defaultFormatter;
  }


  function getVisualizationDivForCellValue(value): any {
    if (value && value.hyperlink) {
      let linkDiv = $("<div>");
      let link = $("<a>").appendTo(linkDiv);
      link.text(value.hyperlink.label);
      if (value.hyperlink.link && value.hyperlink.link.indexOf("https://") === 0) {
        link.attr("href", value.hyperlink.link);
      }
      link.attr("target", "_blank");

      return linkDiv;
    }
    else if (value && value.sparkline) {
      let sparklineDiv = $("<div>");

      var sparklineData = [];
      $(value.sparkline.values).each(function (i, val) {
        var entry = { arg: i, val: val };
        sparklineData.push(entry);
      });

      var sparklineOptions: any = {
        dataSource: sparklineData,
        argumentField: "arg",
        valueField: "val",
        size: { width: 100, height: 22 }
      };

      if (value.sparkline.type) {
        sparklineOptions.type = value.sparkline.type;
      }
      if (value.sparkline.width) {
        sparklineOptions.size.width = value.sparkline.width;
      }
      if (value.sparkline.height) {
        sparklineOptions.size.height = value.sparkline.height;
      }

      ($(sparklineDiv) as any).dxSparkline(sparklineOptions);

      return sparklineDiv;
    }
    else if (value && value.bullet) {
      let bulletDiv = $("<div>");

      var bulletOptions: any = {
        size: { width: 100, height: 22 }
      };

      if (value.bullet.value) {
        bulletOptions.value = value.bullet.value;
      }
      if (value.bullet.target) {
        bulletOptions.target = value.bullet.target;
      }
      if (value.bullet.width) {
        bulletOptions.size.width = value.bullet.width;
      }
      if (value.bullet.height) {
        bulletOptions.size.height = value.bullet.height;
      }
      if (value.bullet.color) {
        bulletOptions.color = value.bullet.color;
      }
      if (value.bullet.targetColor) {
        bulletOptions.targetColor = value.bullet.targetColor;
      }
      if (value.bullet.startScaleValue) {
        bulletOptions.startScaleValue = value.bullet.startScaleValue;
      }

      ($(bulletDiv) as any).dxBullet(bulletOptions);

      return bulletDiv;
    }
    else {
      return "";
    }
  }


  export function getValueTypeString(valueTypeId) {
    return Solovis.Site.ValueTypes.getValueTypeLabel(valueTypeId);
  }


  /**
   * options should contain property, resourcePropertyValue, printMode.
   * @param options
   */
  export function formatResourcePropertyValueAsHtml(options) {
    var propDef = options.property;
    var row = options.resourcePropertyValue;
    const printMode = Boolean(options.forExport) || (Boolean(options.printMode) || null);

    return formatResourcePropertyValueAsHtmlRaw(propDef.propertyTypeId, row.value, row.valueStrings, propDef.tagRefTagSetId, printMode);
  }

  export function formatResourcePropertyValueAsHtmlRaw(
    propertyTypeId: number,
    value: any,
    valueStrings: any[],
    tagSetId: number,
    printMode: boolean) {
    var propTypeIds = InvData.PropertyTypeIds;

    var container = $("<div>");
    if (propertyTypeId == propTypeIds.LongText || propertyTypeId == propTypeIds.String) {
      $(value.s).each(function (i, s: any) {
        var html = $("<div>").text(s + addSeperatorInPrintMode(i, printMode, valueStrings)).html();
        html = html.split("\n").join("<br/>");

        var htmlDiv = $("<div>").html(html)
          .css("border-bottom", "1px solid #ccc");

        if (!printMode) {
          htmlDiv
            .css("min-width", "300px")
            .css("max-height", "125px")
            .css("overflow-y", "auto");
        }

        htmlDiv.appendTo(container);
      });
    }
    else if (propertyTypeId == propTypeIds.Html) {
      $(value.s).each(function (i, s: any) {
        var htmlDiv = $("<div>").html(s);

        if (!printMode) {
          htmlDiv
            .css("min-width", "300px")
            .css("max-height", "125px")
            .css("overflow-y", "auto");
        }

        htmlDiv.appendTo(container);
      });
    }
    else if (propertyTypeId == propTypeIds.Url) {
      $(value.s).each(function (i, url: any) {
        var linkDiv = $("<div>").appendTo(container);

        if (url) {
          // We only allow http(s) links to prevent custom links from being able to execute javascript.
          // We may consider allowing optional whitelisting of other schemes later.

          let urlLower = url.toLowerCase();
          if (urlLower.indexOf("http:") == 0 || urlLower.indexOf("https:") == 0) {
            let link = $("<a>").text(url + addSeperatorInPrintMode(i, printMode, valueStrings)).attr("href", url).appendTo(linkDiv);
          }
          else {
            linkDiv.text(url + addSeperatorInPrintMode(i, printMode, valueStrings));
          }
        }
      });
    }
    else if (propertyTypeId == propTypeIds.TagRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var tagDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var tagLink = $("<a>").text(valueStr).appendTo(tagDiv);

          var tagId = value.t[i];
          var tagRef = { tagId: tagId, tagSetId: tagSetId };

          Solovis.UI.PopupLinkPanel.onElementClick(tagLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getTagLinks(tagRef)
            });
          });
        }
        else {
          tagDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (propertyTypeId == propTypeIds.EntityRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var entityDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var entityLink = $("<a>").text(valueStr).appendTo(entityDiv);

          var entityId = value.e[i];
          var entityRef = { entityId: entityId };

          Solovis.UI.PopupLinkPanel.onElementClick(entityLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getEntityLinks(entityRef)
            });
          });
        }
        else {
          entityDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (propertyTypeId == propTypeIds.InvestmentRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var investmentDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var investmentLink = $("<a>").text(valueStr).appendTo(investmentDiv);

          var resourceId = value.r[i];
          var investmentRef = { resourceId: resourceId };

          Solovis.UI.PopupLinkPanel.onElementClick(investmentLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getInvestmentLinks(investmentRef)
            });
          });
        }
        else {
          investmentDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (propertyTypeId == propTypeIds.ContactRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var contactDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var contactLink = $("<a>").text(valueStr).appendTo(contactDiv);

          var resourceId = value.r[i];
          var contactRef = { resourceId: resourceId };

          Solovis.UI.PopupLinkPanel.onElementClick(contactLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getContactLinks(contactRef)
            });
          });
        }
        else {
          contactDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (propertyTypeId == propTypeIds.NoteRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var noteDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var noteLink = $("<a>").text(valueStr).appendTo(noteDiv);

          var resourceId = value.r[i];
          var noteRef = { resourceId: resourceId };

          Solovis.UI.PopupLinkPanel.onElementClick(noteLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getNoteLinks(noteRef)
            });
          });
        }
        else {
          noteDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (propertyTypeId == propTypeIds.TransactionRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var txDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var txLink = $("<a>").text(valueStr).appendTo(txDiv);

          var resourceId = value.r[i];
          var txRef = { resourceId: resourceId };

          Solovis.UI.PopupLinkPanel.onElementClick(txLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getTransactionLinks(txRef)
            });
          });
        }
        else {
          txDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }



    else if (propertyTypeId == propTypeIds.ManagerRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        const mgrDiv = $("<div>").appendTo(container);
        if (!printMode) {
          let mgrLink = $("<a>").text(valueStr).appendTo(mgrDiv);

          let resourceId = value.r[i];
          let mgrRef = { resourceId: resourceId };

          Solovis.UI.PopupLinkPanel.onElementClick(mgrLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getManagerLinks(mgrRef)
            });
          });
        }
      });
    }
    else if (propertyTypeId == propTypeIds.UserRef) {
      $(valueStrings).each(function (i, valueStr: any) {
        var userDiv = $("<div>").appendTo(container);
        if (!printMode) {
          var userLink = $("<a>").text(valueStr).appendTo(userDiv);

          var userId = value.i[i];
          var userRef = { userId: userId };

          Solovis.UI.PopupLinkPanel.onElementClick(userLink, function (e) {
            Solovis.UI.PopupLinkPanel.showLinks({
              offset: { left: e.pageX, top: e.pageY },
              links: Solovis.Reports.ReportLinker.getUserLinks(userRef)
            });
          });
        }
        else {
          userDiv.text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings));
        }
      });
    }
    else if (valueStrings) {
      $(valueStrings).each(function (i, valueStr: any) {
        if (!printMode) {
          $("<div>")
            .css("overflow", "hidden")
            .css("text-overflow", "ellipsis")
            .text(valueStr)
            .appendTo(container);
        }
        else {
          $("<div>")
            .css("overflow", "hidden")
            .css("text-overflow", "ellipsis")
            .text(valueStr + addSeperatorInPrintMode(i, printMode, valueStrings))
            .appendTo(container);
        }
      });
    }

    return container;
  }

  export function formatCumulativeReturn(value: { value: any, isCumulative: boolean, isCumulativeInterval?: boolean } | null, valueContext: any) {
    const defaultElement = valueContext.defaultFormatter(value?.value, valueContext);

    if (!value || !value.isCumulative) {
      return defaultElement;
    }
    else if (value.isCumulativeInterval) {
      return defaultElement;
    }
    else {
      const div = $("<div>");
      $("<span>").text("cum: ").css("color", "#bbb").appendTo(div);
      div.append(defaultElement);
      return div;
    }
  }

  export function createAssetLabelFormatter(options) {
    var resourceIdProperty = options.resourceIdProperty;

    var formatter = function (value, valueContext) {
      var assetLabel = value;
      if (!assetLabel) {
        return null;
      }

      var row = getCellContextRow(valueContext);

      var assetLink = $("<a>");
      assetLink.text(assetLabel);

      var assetRef: any = {};
      if (resourceIdProperty) {
        assetRef.resourceId = Solovis.resolveObject(resourceIdProperty, row);
      }

      Solovis.UI.PopupLinkPanel.onElementClick(assetLink, function (e) {
        Solovis.UI.PopupLinkPanel.showLinks({
          offset: { left: e.pageX, top: e.pageY },
          links: Solovis.Reports.ReportLinker.getAssetLinks(assetRef),
          autoClickSingleItem: e.type == "click"
        });
      });

      return assetLink;
    };

    return formatter;
  }


  export function createEntityLabelFormatter(options) {
    var entityIdProperty = options.entityIdProperty;

    var formatter = function (value, valueContext) {
      var entityLabel = value;
      if (!entityLabel) {
        return null;
      }

      var row = getCellContextRow(valueContext);

      var assetRef: any = {};
      if (entityIdProperty) {
        assetRef.entityId = row[entityIdProperty];
      }

      if (assetRef.entityId) {
        var assetLink = $("<a>");
        assetLink.text(entityLabel);

        Solovis.UI.PopupLinkPanel.onElementClick(assetLink, function (e) {
          var links = Solovis.Reports.ReportLinker.getEntityLinks(assetRef);

          if (options.extraLinks) {
            var extraLinks = options.extraLinks(value, valueContext);
            if (!_.isEmpty(extraLinks)) {
              links = links.concat(extraLinks);
            }
          }

          Solovis.UI.PopupLinkPanel.showLinks({
            offset: { left: e.pageX, top: e.pageY },
            links: links,
            autoClickSingleItem: e.type == "click"
          });
        });

        return assetLink;
      }
      else {
        return $("<span>").text(entityLabel);
      }
    };

    return formatter;
  }


  export function createInvestmentLabelFormatter(options) {
    var investmentIdProperty = options.investmentIdProperty;

    var formatter = function (value, valueContext) {
      var investmentLabel = value;
      if (!investmentLabel) {
        return null;
      }

      var row = getCellContextRow(valueContext);

      var assetLink = $("<a>");
      assetLink.text(investmentLabel);

      var assetRef: any = {};
      if (investmentIdProperty) {
        assetRef.investmentId = row[investmentIdProperty];
      }

      Solovis.UI.PopupLinkPanel.onElementClick(assetLink, function (e) {
        var links = Solovis.Reports.ReportLinker.getInvestmentLinks(assetRef);

        if (options.extraLinks) {
          var extraLinks = options.extraLinks(value, valueContext);
          if (!_.isEmpty(extraLinks)) {
            links = links.concat(extraLinks);
          }
        }

        Solovis.UI.PopupLinkPanel.showLinks({
          offset: { left: e.pageX, top: e.pageY },
          links: links,
          autoClickSingleItem: e.type === "click"
        });
      });

      return assetLink;
    };

    return formatter;
  }

  export function createPropertyLabelFormatter() {
    return function (cellValue, cellContext) {
      const labelHtml = cellContext.defaultFormatter(cellValue, cellContext);

      const labelDiv = $("<div>");

      const property = cellContext.row.property;

      const actionDiv = $("<div>")
        .addClass("no-print")
        .css("float", "right")
        .css("padding-left", "10px")
        .appendTo(labelDiv);

      $("<a>")
        .html('<span class="fa fa-pencil"></span>')
        .attr("title", "Edit")
        .css({"display": "inline-block", "color": "#444"})
        .appendTo(actionDiv)
        .click(function () {
          Solovis.Properties.PropertyEditor.editResourceProperty({
            property: property,

            saveCallback: ((options) => {
              const dataTable = cellContext.dataTable;
              const dataSets = dataTable.dataSets;
              const property = dataSets.values.properties.find(
                (value) =>
                  value.propertyDefinitionId ===
                  options.propertyDefinitionId
              );

              InvData.Util.updatePropertyValues(property, options.values, property.property.isStaticOverTime
                ? undefined : options.fromDayId);

              dataTable.updateDataSets(dataSets);
            }).bind(cellContext)
          });
        });

      labelDiv.append(labelHtml);
      if (getCellContextRow(cellContext).required) {
        // Required indicator
        $("<span>").text(" * ").css("color", "red").appendTo(labelDiv);
      }

      const help = getCellContextRow(cellContext).helpText;
      if (help) {
        const helpSpan = $("<span>").text(" ").appendTo(labelDiv);
        const helpLink = $("<a>")
          .addClass("fa fa-question-circle")
          .appendTo(helpSpan);
        const portalServices = new Solovis.Portal.SitePortalServices();
        helpLink.click(() => {
          Solovis.Site.HelpManager.showCustomHelpDialog(help, {
            title: labelHtml,
            portalServices: portalServices,
          });
        });
      }

      return labelDiv;
    };
  }

  export function createLiquidityModelLabelFormatter(liquidityModelInfo) {
    const modelLabel = liquidityModelInfo.label;

    if (!modelLabel) {
      return null;
    }

    const modelId = liquidityModelInfo.liquidityModelId;

    const link = $("<a>")
      .text(modelLabel);

    Solovis.UI.PopupLinkPanel.onElementClick(link, function (e) {
      Solovis.UI.PopupLinkPanel.showLinks({
        offset: { left: e.pageX, top: e.pageY },
        links: Solovis.Reports.ReportLinker.getLiquidityModelLink(modelId)
      });
    });

    return link;
  };

  export const propValueHtmlFormatter = function (cellValue, cellContext) {
    const row = getCellContextRow(cellContext);
    const propDef = row.property;
    if (!row.value) {
      InvData.Util.updatePropertyValues(row, []);
    }

    const container = Solovis.Reports.Formatter.formatResourcePropertyValueAsHtml(
      {
        property: propDef,
        resourcePropertyValue: row,
        printMode:
          cellContext.dataTable && cellContext.dataTable.printMode
            ? cellContext.dataTable.printMode
            : null,
      }
    );

    return container;
  };

  export const assetLabelGroupHtmlFormatter = function (value, valueContext) {
    const span = $("<span>");
    if (valueContext.row?._backingRow?.data?.assetLabel) {
      span.text(valueContext.row._backingRow.data.assetLabel);
    } else if (valueContext.group?.label) {
      span.text(valueContext.group.label + " (Other)");
    }
    return span;
  };

  export function getCellContextRow(context) {
    const row = !context.group ? context.row :
      context.row._rows ? context.row._rows[0] : undefined;

    return row;
  }

  /**
   * Formats manager labels as clickable links with context menu
   * @param cellValue - The cell value to format
   * @param cellContext - Context information including row data
   * @returns jQuery element containing the formatted link
   */
  export const managerLabelFormatter = function (cellValue: string, cellContext: any) {
    const label = cellContext.defaultFormatter(cellValue, cellContext);
    if (cellContext.dataTable && cellContext.dataTable.reportControl) {
      if (cellContext.dataTable.reportControl.printMode) {
        return label;
      }
    }
    
    const managerId = cellContext.row.id;

    const managerLink = $("<a>")
      .html(label);

    Solovis.UI.PopupLinkPanel.onElementClick(managerLink, function (e) {
      Solovis.UI.PopupLinkPanel.showLinks({
        offset: { left: e.pageX, top: e.pageY },
        links: Solovis.Reports.ReportLinker.getManagerLinks(managerId)
      });
    });

    return managerLink;
  };
}

